// PracticeLostBanner — the one-line notice after a refused practice (ai.346, D-2026-10-08-07 S1 A; fix round DR1,
// DESIGN_REVIEW_2026-10-08_ai346-practice-switch-refusal.md L2-1, L3-1, L4-1, L4-2, L5-1, L8-1, L9-1, L9-2, L9-3).
//
// When the server refuses the selected practice (403 PRACTICE_SWITCH_DENIED), the API interceptor (lib/api.ts)
// writes an OWNED record { user_id, practice_id, name, at } (lib/practiceLostRecord), sets the page flag, clears the
// selection and reloads. This strip says what happened, in one of the review's three measured variants:
//   V3  the page flag is set: the reload has not happened (cancelled at "Reload site?", or still pending) and nothing
//       but sign-in traffic leaves the browser. Its name comes from the flag (same page, same signed-in user); the
//       record is left for the next page. It stays until Dismiss or the reload, also after a practice is picked.
//   V1  after a completed reload, a practice selected, not Platform view.
//   V2  after a completed reload, no practice selected or Platform view (also the loop breaker's clear).
// The record is shown only to its owner (exact user_id) and only while fresh. Shown means consumed; another user's,
// stale or unreadable records are removed silently; while the store user is unknown it is kept. A record of the
// practice that is selected again is kept for the breaker: shown if the breaker then clears the selection, removed
// silently if another practice is picked (that practice worked after the reload, so "has ended" would be untrue).
// V1 and V2 hide once the selection moves away from the practice they were shown for. A practice name is not PHI.

import React, { useEffect, useRef, useState } from 'react';
import { AlertTriangle, X } from 'lucide-react';
import { useAuthStore } from '../../store';
import {
  PRACTICE_LOST_NOTICE_TTL_MS, getPracticeReloadPending, readPracticeLostRecord, removePracticeLostRecord,
} from '../../lib/practiceLostRecord';

/** L9-1 — the always-mounted live region receives the sentence this long after the strip is shown. */
const ANNOUNCE_DELAY_MS = 500;

type Notice =
  | { variant: 'V3'; lost: string }
  | { variant: 'V1' | 'V2'; lost: string; shownFor: string | null };

interface PracticeLostBannerProps {
  /** The shell's <main> (tabIndex -1): Dismiss parks focus there instead of dropping it to <body> (L9-2). */
  mainRef: React.RefObject<HTMLElement>;
}

export default function PracticeLostBanner({ mainRef }: PracticeLostBannerProps) {
  const user = useAuthStore((s) => s.user);
  const currentPractice = useAuthStore((s) => s.currentPractice);
  const platformMode = useAuthStore((s) => s.platformMode);
  const [notice, setNotice] = useState<Notice | null>(null);
  // L9-1 (D-2026-10-15-04) — the notice the live region was timed for. The region speaks only while it is the CURRENT
  // notice, so a new notice (V3 replacing an announced V1) is never announced in the commit that first shows it.
  const [announcedNotice, setAnnouncedNotice] = useState<Notice | null>(null);
  // The record evaluation stops once a strip was shown or dismissed in this mount. V3 is shown at most once.
  const settledRef = useRef(false);
  const reloadNoticeShownRef = useRef(false);
  // R5 — the `at` of a record this mount kept because its practice was the selected one.
  const keptAtRef = useRef<number | null>(null);
  const dismissRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const pendingName = getPracticeReloadPending();
    if (pendingName !== null) {
      // V3 — the reload has not happened. The record is never read, written or removed here.
      if (!reloadNoticeShownRef.current) {
        reloadNoticeShownRef.current = true;
        settledRef.current = true;
        setNotice({ variant: 'V3', lost: pendingName });
      }
      return;
    }
    if (settledRef.current || !user) return; // no owner known yet: the record is kept
    const record = readPracticeLostRecord();
    if (record === null) return;
    if (record === 'invalid' || record.user_id !== user.user_id) {
      removePracticeLostRecord();
      return;
    }
    const age = Date.now() - record.at;
    if (!(age >= 0 && age <= PRACTICE_LOST_NOTICE_TTL_MS)) {
      removePracticeLostRecord();
      return;
    }
    if (currentPractice && currentPractice.practice_id === record.practice_id) {
      keptAtRef.current = record.at; // the breaker reads it on the next refusal of this practice
      return;
    }
    removePracticeLostRecord();
    if (currentPractice && keptAtRef.current === record.at) return; // R5
    settledRef.current = true;
    setNotice({
      variant: !currentPractice || platformMode ? 'V2' : 'V1',
      lost: record.name,
      shownFor: currentPractice ? currentPractice.practice_id : null,
    });
  }, [user, currentPractice, platformMode]);

  // L4-1 — V1 and V2 belong to the practice they were shown for; V3 stays until Dismiss or the reload (R2).
  // Hidden at render (no frame names the wrong practice), then dropped for good by a FUNCTIONAL update, so it never
  // clears a notice set in the same commit (a refusal that turns a V1 into V3).
  const selectedId = currentPractice ? currentPractice.practice_id : null;
  const visible = notice !== null && (notice.variant === 'V3' || notice.shownFor === selectedId);
  useEffect(() => {
    setNotice((n) => (n && n.variant !== 'V3' && n.shownFor !== selectedId ? null : n));
  }, [selectedId]);

  useEffect(() => {
    if (!notice) return undefined;
    const timer = window.setTimeout(() => setAnnouncedNotice(notice), ANNOUNCE_DELAY_MS);
    return () => window.clearTimeout(timer);
  }, [notice]);

  // ACC-F17 A (D-2026-10-23-04) - a NAMELESS practice (name '') reads "the practice you were in", its second mention "that practice" (the founder's word supersedes the D-2026-10-15-04 wording; behaviour unchanged).
  const nameless = notice?.lost === '';
  const lostName = nameless ? 'the practice you were in' : notice?.lost;
  const lostAgain = nameless ? 'that practice' : notice?.lost;
  let lead = '';
  let rest = '';
  if (notice?.variant === 'V3') {
    lead = `Your access to ${lostName} has ended.`;
    rest = ' Reload the page to continue.';
  } else if (notice?.variant === 'V1') {
    lead = `Your access to ${lostName} has ended;`;
    rest = ` you are now in ${currentPractice?.name ?? ''}. Saved work stays at ${lostAgain}; anything unsaved on screen was lost.`;
  } else if (notice) {
    lead = `Your access to ${lostName} has ended.`;
    rest = ` Saved work stays at ${lostAgain}; anything unsaved on screen was lost.`;
  }

  const dismiss = () => {
    // L9-2 — the button is about to unmount: park focus on <main> without moving the view.
    if (dismissRef.current && document.activeElement === dismissRef.current) {
      mainRef.current?.focus({ preventScroll: true });
    }
    setNotice(null);
  };

  return (
    <>
      {/* L9-1 — always mounted, so the sentence is announced when it arrives (imports-r3c F9 pattern). */}
      <div role="status" className="sr-only">{visible && announcedNotice === notice ? lead + rest : ''}</div>
      {visible && notice && (
        <div className="bg-amber-50 border-b border-amber-200 px-4 py-2 flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 text-amber-700 flex-shrink-0 mt-0.5" aria-hidden="true" />
          <span className="flex-1 text-sm text-amber-900 leading-snug">
            <strong>{lead}</strong>
            {rest}
            {notice.variant === 'V3' && (
              <>
                {' '}
                <button type="button" onClick={() => window.location.reload()} className="underline font-medium">
                  Reload page
                </button>
              </>
            )}
          </span>
          <button
            ref={dismissRef}
            type="button"
            onClick={dismiss}
            aria-label="Dismiss"
            className="text-amber-700 hover:text-amber-900 flex-shrink-0"
          >
            <X className="w-4 h-4" aria-hidden="true" />
          </button>
        </div>
      )}
    </>
  );
}
